/* eslint-disable no-unused-vars */
import './style.scss';
import PropTypes, { func } from 'prop-types';

import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';

import { Accordion, AccordionTab } from 'primereact/accordion';
import { openRevises } from 'apis/projectProposals.api';
import { createRequestUpdateValidationSchema } from 'features/ReviseProjectProposal/validation';
import { FileInput, TextareaInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TIMEZONE_VN, TOAST } from 'constant';
import { toast } from 'layout';
import { checkOverdueDatetime, formatFileName, getMaxDate, mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createRequestUpdateValidationSchema) };

const OpenRevisesForm = forwardRef(
  ({ selectedItems, unselectedItems, isSelectedAll, isUnselectedSome, filters }, ref) => {
    // #region Data
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
    const [requestControl, setRequestControl] = useState(null);

    const {
      handleSubmit,
      control,
      trigger,
      reset,
      setValue,
      watch,
      formState: { errors, dirtyFields },
    } = useForm({ ...formOptions, mode: 'onChange' });

    const {
      mutate: mutateOpenRevises,
      isLoading: isOpenRevisesLoading,
      isError: isOpenRevisesError,
      error: openRevisesError,
    } = useMutation(openRevises);

    // #endregion

    // #region Event
    useImperativeHandle(
      ref,
      () => ({
        open: (_requestControl) => {
          setRequestControl(_requestControl);
          setVisible(true);
        },
      }),
      []
    );

    const onSubmit = ({ comment, deadline, commentFiles }) => {
      const formData = new FormData();
      const newData = {
        filters: {
          selectedItems,
          unselectedItems,
          isSelectedAll,
          isUnselectedSome,
          ...filters,
        },
        comment,
        commentFiles,
      };

      if (commentFiles?.length > 0) {
        commentFiles?.forEach((file) => {
          formData.append('commentFiles', file);
        });
      }
      if (comment) {
        formData.append('comment', comment);
      }

      formData.append('filters', JSON.stringify(newData.filters));

      mutateOpenRevises(formData, {
        onSuccess: ({ data }) => {
          setConfirmDialogVisible(false);
          if (data.success) {
            toast(TOAST.SUCCESS, t('projectProposal.updateUpdateRequest.success'));
            setVisible(false);
            reset();
          } else {
            setValue('failData', data.errorLogs);
            toast(TOAST.WARN, t('projectProposal.updateUpdateRequest.notSuccess'));
          }
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    };

    const checkFormValid = async () => {
      const isValid = await trigger();
      if (!isValid) {
        toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
      } else {
        setConfirmDialogVisible(true);
      }
    };
    // #endregion

    // #region Format history
    const formatHeader = (value, rowIndex) => {
      const deadline = getMaxDate(
        requestControl?.value?.organizationPrequalificationDeadline,
        requestControl?.value?.program?.organizationPrequalificationDeadline
      );

      let statusJsx = null;
      if (!value?.isOpened) {
        statusJsx = (
          <span className="font-medium text-green-500 text-base font-italic">
            {t('projectProposal.requestUpdate.ownerAddedExplanation')}
          </span>
        );
      } else if (value?.isOpened && checkOverdueDatetime(deadline)) {
        statusJsx = (
          <span className="font-medium text-red-500 text-base font-italic">
            {t('projectProposal.requestUpdate.overdueDeadline')}
          </span>
        );
      } else {
        statusJsx = (
          <span className="font-medium text-red-500 text-base font-italic">
            {t('projectProposal.requestUpdate.deadline')}:{' '}
            {deadline
              ? new Date(deadline).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })
              : t('projectProposal.requestUpdate.noDeadline')}
          </span>
        );
      }

      return (
        <div className="flex flex-column text-lg font-bold gap-1">
          <span>
            {rowIndex + 1}.{' '}
            {new Date(value?.createdAt).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })}
          </span>
          <div>{statusJsx}</div>
        </div>
      );
    };
    const formatComment = (value) => (
      <div className="mb-2">
        <span className="font-semibold">{t('projectProposal.requestUpdate.comment')}:</span>
        <p className="my-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
          {value?.comment}
        </p>
      </div>
    );
    const formatCommentFileUrls = (value) => (
      <div className="mb-2">
        <span className="mr-2 font-semibold">
          {t('projectProposal.requestUpdate.commentFile')}:
        </span>
        {value?.commentFileUrls?.length > 0 && (
          <ul className="list-disc">
            {value?.commentFileUrls?.map((item, index) => (
              <li className="-ml-4" key={item?.id ?? index}>
                <span className="p-button p-button-text p-button-sm p-0" onClick={() => true}>
                  <span className="text-blue-500 underline font-italic text-base">
                    {formatFileName(item?.name)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
    // #endregion
    const failData = watch('failData') || [];
    return (
      <Dialog
        header={
          failData?.length === 0
            ? t('projectProposal.requestUpdate.title')
            : t('projectProposal.requestExplanation.failTable.title')
        }
        draggable={false}
        visible={visible}
        style={{ width: failData?.length === 0 ? '750px' : '1000px' }}
        onHide={() => {
          // hide the dialog first, then reset the historyVisible state shortly after
          // to avoid slight glitch when closing the dialog
          setVisible(false);
          setTimeout(() => {
            reset();
          }, 200);
        }}
        closable={!isOpenRevisesLoading}
      >
        <div className="relative">
          {failData?.length === 0 && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="p-fluid flex flex-column"
              autoComplete="off"
            >
              <div className="grid">
                <div className="col-12 mb-2">
                  <TextareaInput
                    label={t('projectProposal.requestUpdate.comment')}
                    name="comment"
                    control={control}
                    errors={errors}
                    row={5}
                    isRequired
                    disabled={isOpenRevisesLoading}
                  />
                </div>

                <div className="col-12 mb-2">
                  <label htmlFor="commentFiles">
                    {t('projectProposal.requestUpdate.commentFile')}{' '}
                  </label>
                  <FileInput
                    name="commentFiles"
                    control={control}
                    errors={errors}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                    acceptOnly="pdfWordExcelOnly"
                    multiple
                    disabled={isOpenRevisesLoading}
                  />
                </div>
              </div>

              {/* align right */}
              <div className="mt-2 flex justify-content-end">
                <div className="col-6">
                  <Button
                    className="ml-auto"
                    label={t('formLayout.action.cancel')}
                    type="button"
                    severity="danger"
                    onClick={() => {
                      reset();
                      setVisible(false);
                    }}
                    disabled={isOpenRevisesLoading}
                  />
                </div>
                <div className="col-6">
                  <Button
                    label={t('projectProposal.requestExplanation.sendRequest')}
                    className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                    type="button"
                    onClick={checkFormValid}
                    disabled={isOpenRevisesLoading || !(Object.keys(dirtyFields)?.length > 0)}
                  />
                </div>
              </div>

              {/*
                confirm dialog: use this instead of using .requestSubmit() of form,
                avoid browser compatibility error
              */}
              <div
                className={` ${
                  confirmDialogVisible ? '' : 'hidden '
                } p-dialog-mask p-dialog-center p-component-overlay p-component-overlay-enter p-dialog-visible p-dialog-resizable p-dialog-draggable`}
              >
                <div
                  className="p-dialog p-component p-dialog-default p-dialog-enter-done"
                  role="dialog"
                  aria-modal="true"
                  style={{ width: '450px' }}
                >
                  <div className="p-dialog-header">
                    <div className="p-dialog-title">
                      <p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>
                    </div>
                    <div className="p-dialog-header-icons">
                      <Button
                        type="button"
                        className="p-dialog-header-icon p-dialog-header-close p-link"
                        aria-label="Close"
                        onClick={() => setConfirmDialogVisible(false)}
                        disabled={isOpenRevisesLoading}
                      >
                        <span
                          className="p-dialog-header-close-icon pi pi-times"
                          aria-hidden="true"
                        />
                        <span role="presentation" className="p-ink" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-dialog-content">
                    <p className="text-lg font-bold">
                      {t('projectProposal.requestExplanation.infoMessage')}
                    </p>
                    <p className="text-lg">
                      {t('projectProposal.requestExplanation.confirmationMessage')}
                    </p>
                  </div>
                  <div className="p-dialog-footer">
                    <div>
                      <Button
                        label={t('projectProposal.cancel')}
                        className="w-7rem"
                        severity="danger"
                        onClick={() => setConfirmDialogVisible(false)}
                        autoFocus
                        disabled={isOpenRevisesLoading}
                        type="button"
                      />
                      <Button
                        label={t('projectProposal.ok')}
                        className="w-7rem "
                        severity="info"
                        disabled={isOpenRevisesLoading}
                        type="submit"
                      />
                    </div>
                  </div>
                  <span className="p-resizable-handle" style={{ zIndex: 90 }} />
                </div>
              </div>
            </form>
          )}
          {failData?.length > 0 && (
            <>
              <span className="underline" style={{ color: 'blue' }}>
                {t('projectProposal.requestExplanation.failTable.failMessage')}
              </span>
              <div className="table-container mt-4">
                <div className="form-table">
                  <table className="mb-2">
                    <thead>
                      <tr>
                        <th className="text-center" style={{ minWidth: 5, width: 5 }}>
                          {t('projectProposal.requestExplanation.failTable.no')}
                        </th>
                        <th className="text-center" style={{ minWidth: 300, width: 300 }}>
                          {t('projectProposal.requestExplanation.failTable.vietnameseName')}
                        </th>
                        <th className="text-center" style={{ minWidth: 20, width: 20 }}>
                          {t('projectProposal.requestExplanation.failTable.type')}
                        </th>
                        <th className="text-center" style={{ minWidth: 130, width: 130 }}>
                          {t('projectProposal.requestExplanation.failTable.reason')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {failData?.length > 0 &&
                        failData?.map((item, index) => (
                          <tr key={item.reference}>
                            <td className="px-1 text-center">{index + 1}</td>
                            <td className="px-3">{item?.name}</td>
                            <td className="px-3">{item?.projectType}</td>
                            <td className="px-3">
                              {t(
                                `projectProposal.requestExplanation.failTable.errors.${item?.code}` ||
                                  item?.msg
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {isOpenRevisesLoading && <Loading />}
      </Dialog>
    );
  }
);

OpenRevisesForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedItems: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  unselectedItems: PropTypes.array.isRequired,
  isSelectedAll: PropTypes.bool.isRequired,
  isUnselectedSome: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.object.isRequired,
};

export default OpenRevisesForm;
